import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";

const serviceImages = {
  branding: "https://images.pexels.com/photos/9121155/pexels-photo-9121155.jpeg",
  digital: "https://images.unsplash.com/photo-1612544409025-e1f6a56c1152",
  interior: "https://images.pexels.com/photos/37793325/pexels-photo-37793325.jpeg",
  fashion: "https://images.pexels.com/photos/34221129/pexels-photo-34221129.jpeg",
  music: "https://images.unsplash.com/photo-1612544409025-e1f6a56c1152",
  marketing: "https://images.unsplash.com/photo-1637250096679-c10f2751def8",
  graphic: "https://images.pexels.com/photos/9121155/pexels-photo-9121155.jpeg",
};

export default function Services() {
  const { t } = useLang();
  const [hovered, setHovered] = useState(null);
  const [cursor, setCursor] = useState({ x: 0, y: 0 });

  return (
    <section
      id="services"
      className="relative py-32 md:py-48 px-6 md:px-12 bg-[#050505] border-t hairline"
      data-testid="services-section"
      onMouseMove={(e) => setCursor({ x: e.clientX, y: e.clientY })}
    >
      <div className="max-w-[1600px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-20 items-end">
          <div className="md:col-span-6">
            <div className="flex items-center gap-4 mb-8">
              <span className="inline-block w-8 h-px bg-[color:var(--accent)]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[color:var(--muted)]">
                {t.services.label}
              </span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="font-display uppercase text-5xl md:text-7xl lg:text-8xl font-extrabold leading-[0.9] tracking-[-0.03em]"
            >
              {t.services.title}
            </motion.h2>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <p className="text-base md:text-lg text-[color:var(--muted)] leading-relaxed">
              <span className="font-serif-italic text-[color:var(--fg)]">
                Seven practices,{" "}
              </span>
              one studio. Every discipline draws from every other &mdash; that&apos;s the point.
            </p>
          </div>
        </div>

        <ul className="border-t hairline">
          {t.services.items.map((item, i) => (
            <li
              key={item.id}
              className="group relative border-b hairline"
              onMouseEnter={() => setHovered(item.id)}
              onMouseLeave={() => setHovered(null)}
              data-testid={`service-item-${item.id}`}
            >
              <Link
                to={`/services/${item.id}`}
                className="block"
                data-testid={`service-link-${item.id}`}
              >
                <div className="grid grid-cols-12 items-center py-8 md:py-12 gap-6 transition-all duration-500 group-hover:pl-6">
                  <div className="col-span-1 text-xs uppercase tracking-[0.25em] text-[color:var(--muted)]">
                    0{i + 1}
                  </div>
                  <div className="col-span-7 md:col-span-5">
                    <h3 className="font-display uppercase text-3xl md:text-5xl lg:text-6xl font-bold tracking-[-0.03em] leading-none transition-colors duration-500 group-hover:text-[color:var(--accent)]">
                      {item.name}
                    </h3>
                  </div>
                  <div className="hidden md:block md:col-span-5 text-[color:var(--muted)] text-sm md:text-base leading-relaxed">
                    {item.desc}
                  </div>
                  <div className="col-span-4 md:col-span-1 text-right">
                    <span
                      className="inline-block text-xl md:text-2xl transition-transform duration-500 group-hover:translate-x-2 group-hover:text-[color:var(--accent)]"
                      aria-hidden
                    >
                      →
                    </span>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Floating image */}
      <AnimatePresence>
        {hovered && (
          <motion.div
            key={hovered}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed pointer-events-none z-30 w-64 h-80 hidden lg:block overflow-hidden"
            style={{
              left: cursor.x + 40,
              top: cursor.y - 200,
            }}
          >
            <img
              src={serviceImages[hovered]}
              alt=""
              className="w-full h-full object-cover"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
